import { Grid, InputAdornment, Typography } from "@mui/material";
import { ExternalLink } from "ExternalLink";
import { OLPaper } from "OLPaper";
import { useWallets } from "api/wallets";
import { useAppSelector } from "app/hooks";
import { ItemTypeCheckbox } from "components/FormControls/ItemTypeCheckbox";
import { NumberFormatInput } from "components/FormControls/NumberFormatInput";
import { RequiredFieldLabel } from "components/FormControls/RequiredFieldLabel";
import { SelectInput } from "components/FormControls/Select/Select";
import { TextInput } from "components/FormControls/TextInput";
import { useFormikContext } from "formik";
import { useMessage } from "hooks/intl";
import { useManageInfiniteScroll } from "hooks/useManageInfiniteScroll";
import { useState } from "react";
import { selectCurrentStoreData } from "reduxState/currentStoreData/currentStoreDataSlice";
import { getTenantSettings } from "reduxState/settings/settings";
import {
  CouponType,
  CouponValueSetting,
  RewardFields,
  RewardType,
} from "types/rewards";
import { pickPluralizedUnit } from "utilities/strings";
import { useStyles } from "./RewardForm.styles";

interface RewardTypeCheckbox {
  reward: RewardType;
  label: string;
  description: string;
}

interface CouponTypeCheckbox {
  coupon: CouponType;
  label: string;
  description: string;
}

interface CouponValueSettingCheckbox {
  coupon: CouponValueSetting;
  label: string;
  description: string;
}

type Props = {
  editMode?: boolean;
  activeReward: RewardType;
  setActiveReward: React.Dispatch<React.SetStateAction<RewardType>>;
  activeCouponType: CouponType;
  setActiveCouponType: React.Dispatch<React.SetStateAction<CouponType>>;
  activeCouponValueSetting: CouponValueSetting;
  setActiveCouponValueSetting: React.Dispatch<
    React.SetStateAction<CouponValueSetting>
  >;
};

export function TypeOfReward({
  editMode,
  activeReward,
  setActiveReward,
  activeCouponType,
  setActiveCouponType,
  activeCouponValueSetting,
  setActiveCouponValueSetting,
}: Props) {
  const classes = useStyles();
  const { message } = useMessage();
  const { currency } = useAppSelector(selectCurrentStoreData);
  const { values } = useFormikContext<RewardFields>();
  const [walletTypeSearch, setWalletTypeSearch] = useState("");
  const settings = useAppSelector(getTenantSettings);

  const rewardWalletCode = settings?.rewardWalletCode;
  const showPriceInCurrencyInput =
    activeReward === "material" ||
    (activeCouponType === "money" &&
      activeCouponValueSetting === "static_coupon");

  const { useInfiniteWalletsList, useWalletTypeList } = useWallets();

  const {
    data: infiniteWalletsList,
    size: walletsSize,
    setSize: setWalletsSize,
    isValidating,
  } = useInfiniteWalletsList({
    filters: walletTypeSearch
      ? [
          {
            condition: "like",
            query: "name",
            value: walletTypeSearch,
          },
          {
            condition: "eq",
            query: "active",
            value: true,
          },
        ]
      : [
          {
            condition: "eq",
            query: "active",
            value: true,
          },
        ],
    order: "asc",
    sortKey: "name",
  });

  const { infiniteData: walletsList, anyElementsToFetch: anyWalletsToFetch } =
    useManageInfiniteScroll(infiniteWalletsList || []);

  const rewardConfigurationWallet = walletsList.filter(
    (wallet) => wallet?.code === rewardWalletCode,
  )[0];

  const walletCode = values?.sourceWalletTypeCode;
  const { data: loadedWalletData } = useWalletTypeList({
    filters: [
      {
        condition: "eq",
        query: "code",
        value: walletCode || "",
      },
    ],
  });

  const loadedWallet = loadedWalletData?.items?.[0];

  const rewardTypes: RewardTypeCheckbox[] = [
    {
      reward: "material",
      label: message({ id: "materialReward" }),
      description: message({ id: "materialRewardInfo" }),
    },
    {
      reward: "coupon",
      label: message({ id: "coupon" }),
      description: message({ id: "couponInfo" }),
    },
  ];

  const couponTypes: CouponTypeCheckbox[] = [
    {
      coupon: "money",
      label: message({ id: "valueCoupon" }),
      description: message({ id: "valueCouponInfo" }),
    },
    {
      coupon: "conversion_coupon",
      label: message({ id: "unitsConversionCoupon" }),
      description: message({ id: "unitsConversionCouponInfo" }),
    },
    {
      coupon: "percentage",
      label: message({ id: "percentageCoupon" }),
      description: message({ id: "percentageCouponInfo" }),
    },
  ];

  const couponValueSettings: CouponValueSettingCheckbox[] = [
    {
      coupon: "static_coupon",
      label: message({ id: "fixedValue" }),
      description: message({ id: "fixedValueInfo" }),
    },
    {
      coupon: "dynamic_coupon",
      label: message({ id: "dynamicValue" }),
      description: message({ id: "dynamicValueInfo" }),
    },
  ];

  return (
    <OLPaper className={classes.OLPaper}>
      <Grid container spacing={12}>
        {rewardTypes.map((rewardType: RewardTypeCheckbox, index: number) => (
          <Grid
            item
            xs={6}
            key={index}
            onClick={() => {
              if (editMode) return;

              setActiveReward(rewardType.reward);
              if (rewardType.reward === "coupon") {
                setActiveCouponValueSetting("static_coupon");
                return setActiveCouponType("money");
              }
              setActiveCouponType(undefined);
            }}
            data-test-id={`reward-form-${rewardType.reward}-reward-box`}
          >
            <ItemTypeCheckbox
              activeItem={activeReward === rewardType.reward}
              label={rewardType.label}
              description={rewardType.description}
              disabled={editMode}
              data-test-id={`reward-type-${rewardType.reward}`}
            />
          </Grid>
        ))}
      </Grid>
      {activeReward !== "material" && (
        <Grid container columnSpacing={12}>
          <Grid item xs={12}>
            <Typography className={classes.sectionHeader}>
              {message({ id: "couponType" })}
            </Typography>
          </Grid>
          {couponTypes.map((couponType: CouponTypeCheckbox, index: number) => (
            <Grid
              item
              xs={6}
              className={classes.checkboxTopPadding}
              key={index}
              onClick={() =>
                !editMode && setActiveCouponType(couponType.coupon)
              }
              data-test-id={`reward-form-${couponType.coupon}-box`}
            >
              <ItemTypeCheckbox
                activeItem={activeCouponType === couponType.coupon}
                label={couponType.label}
                description={couponType.description}
                disabled={editMode}
                data-test-id={`reward-coupon-${couponType.coupon}`}
              />
            </Grid>
          ))}
          <Grid item xs={12}>
            <Typography className={classes.inputInfo}>
              {message(
                { id: "learnMoreAboutCouponTypes" },
                {
                  learnMore: (
                    <ExternalLink
                      className={classes.link}
                      path="https://help.openloyalty.io/main-features/rewards/reward-list/reward-types"
                      data-test-id="reward-form-coupon-types-learn-more-link"
                    >
                      {message({ id: "learnMore" })}
                    </ExternalLink>
                  ),
                },
              )}
            </Typography>
          </Grid>
        </Grid>
      )}
      {activeReward !== "material" && activeCouponType === "money" && (
        <Grid container spacing={12}>
          <Grid item xs={12}>
            <Typography className={classes.sectionHeader}>
              {message({ id: "couponValueSettings" })}
            </Typography>
          </Grid>
          {couponValueSettings.map(
            (couponValueSetting: CouponValueSettingCheckbox, index: number) => (
              <Grid
                item
                xs={6}
                className={classes.checkboxTopPadding}
                key={index}
                onClick={() =>
                  !editMode &&
                  setActiveCouponValueSetting(couponValueSetting.coupon)
                }
                data-test-id={`reward-form-${couponValueSetting.coupon}-box`}
              >
                <ItemTypeCheckbox
                  activeItem={
                    activeCouponValueSetting === couponValueSetting.coupon
                  }
                  label={couponValueSetting.label}
                  description={couponValueSetting.description}
                  disabled={editMode}
                  data-test-id={`reward-${couponValueSetting.coupon}`}
                />
              </Grid>
            ),
          )}
          {activeCouponValueSetting === "static_coupon" && (
            <Grid item xs={12}>
              <NumberFormatInput
                name="couponValue"
                label={
                  <RequiredFieldLabel text={message({ id: "couponValue" })} />
                }
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">{currency}</InputAdornment>
                  ),
                }}
                allowDecimals
                data-test-id="reward-form-static-coupon-coupon-value-input"
              />
            </Grid>
          )}
        </Grid>
      )}
      {activeReward !== "material" && activeCouponType === "percentage" && (
        <TextInput
          className={classes.fullWidth}
          InputProps={{
            endAdornment: <InputAdornment position="end">%</InputAdornment>,
          }}
          label={<RequiredFieldLabel text={message({ id: "couponValue" })} />}
          name="couponValue"
          type="number"
          data-test-id="reward-form-percentage-coupon-coupon-value-input"
        />
      )}
      <Grid container>
        <Grid item xs={12}>
          <SelectInput
            displaySearch
            label={message({
              id: "walletTypeAllowedForRedemption",
            })}
            name="sourceWalletTypeCode"
            placeholder={message({
              id: "walletTypeAllowedForRedemption",
            })}
            data-test-id="reward-form-wallet-type-input"
            width="100%"
            items={walletsList.map((wallet) => ({
              label: wallet?.name,
              id: wallet?.code,
            }))}
            asyncSearch={(searchedQuery) => setWalletTypeSearch(searchedQuery)}
            initialValue={
              loadedWallet && {
                id: loadedWallet?.code,
                label: loadedWallet?.name,
              }
            }
            loadMoreElements={() =>
              !isValidating && setWalletsSize(walletsSize + 1)
            }
            anyElementsToFetch={anyWalletsToFetch}
          />
        </Grid>
        <Grid item xs={12}>
          <Typography className={classes.inputInfo}>
            {message({ id: "rewardWalletTypeInfo" })}
          </Typography>
        </Grid>
      </Grid>
      {showPriceInCurrencyInput && (
        <NumberFormatInput
          name="price"
          label={message(
            { id: "priceInCurrency" },
            {
              currency,
            },
          )}
          allowDecimals
          data-test-id="reward-form-reward-price-in-currency-input"
        />
      )}
      {activeCouponType !== "conversion_coupon" && (
        <NumberFormatInput
          name="costInPoints"
          label={message(
            { id: "rewardPriceInUnits" },
            {
              units:
                loadedWallet?.unitPluralName ||
                rewardConfigurationWallet?.unitPluralName,
            },
          )}
          data-test-id="reward-form-reward-price-in-units-input"
        />
      )}
      {activeCouponType === "conversion_coupon" && (
        <>
          <Grid container>
            <Grid item xs={12}>
              <TextInput
                className={classes.fullWidth}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {pickPluralizedUnit(
                        values.unitsConversion?.ratio as number,
                        loadedWallet?.unitSingularName ||
                          rewardConfigurationWallet?.unitSingularName,
                        loadedWallet?.unitPluralName ||
                          rewardConfigurationWallet?.unitPluralName,
                      )}
                    </InputAdornment>
                  ),
                }}
                label={
                  <RequiredFieldLabel
                    text={message(
                      { id: "unitsConversionRatio" },
                      {
                        units:
                          loadedWallet?.unitPluralName ||
                          rewardConfigurationWallet?.unitPluralName,
                      },
                    )}
                  />
                }
                name="unitsConversion.ratio"
                type="number"
                data-test-id="reward-form-units-conversion-ratio-input"
              />
            </Grid>
            <Grid item xs={12}>
              <Typography className={classes.inputInfo}>
                {message(
                  { id: "unitsConversionRatioInfo" },
                  {
                    units:
                      loadedWallet?.unitPluralName ||
                      rewardConfigurationWallet?.unitPluralName,
                    currency,
                  },
                )}
              </Typography>
            </Grid>
          </Grid>
          <SelectInput
            className={classes.fullWidth}
            items={[
              {
                id: "default",
                label: message({ id: "none" }),
              },
              {
                id: "up",
                label: message({
                  id: "roundup",
                }),
              },
              {
                id: "down",
                label: message({
                  id: "rounddown",
                }),
              },
            ]}
            label={
              <RequiredFieldLabel
                text={message(
                  { id: "unitsConversionRounding" },
                  {
                    units:
                      loadedWallet?.unitPluralName ||
                      rewardConfigurationWallet?.unitPluralName,
                  },
                )}
              />
            }
            name="unitsConversion.rounding"
            data-test-id="reward-form-units-conversion-rounding-input"
          />
        </>
      )}
    </OLPaper>
  );
}
